import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SmartFirstSendWarning } from '../../components/smart/FirstSendWarning';
import { SmartShell } from '../../components/smart/SmartShell';
import { Stepper } from '../../components/smart/Stepper';
import { Button } from '../../components/smart/ui/Button';
import { formatCurrency } from '../../components/smart/utils/format';
import { SMART_ROUTES } from '../../constants';
import { isFirstSendWarningVisible } from '../../gallery/firstSendMock';
import {
  smartMockCollect,
  smartMockCompose,
  smartMockFirstSendQuote,
  smartMockQuote,
} from '../../gallery/smartMock';
import { useGalleryFirstSend } from '../../hooks/useGalleryFirstSend';
import { FUNDING_METHOD_CATEGORY } from '../../utils/sendCatalog';

/**
 * Gallery-static collect summary — seeded quote + compose/collect, confirm
 * advances to the KYC intro gate (CIP chain), matching the live wizard phase.
 */
export const CollectSummaryPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();
  const firstSend = useGalleryFirstSend();
  const quote = firstSend ? smartMockFirstSendQuote : smartMockQuote;
  const compose = smartMockCompose;
  const collect = smartMockCollect;
  const showFirstSendWarning =
    firstSend != null &&
    isFirstSendWarningVisible({
      sendAmount: quote.sendAmount,
      sourceCurrency: quote.sendCurrency,
      fundingCategory: compose.funding
        ? FUNDING_METHOD_CATEGORY[compose.funding]
        : null,
    });

  return (
    <SmartShell
      title={t('wizard.headline.collect.title.default')}
      subtitle={t('wizard.headline.collect.subtitle')}
    >
      <div className="flex flex-col gap-5">
        <Stepper
          steps={[
            { id: 'recipient_identity', label: t('wizard.collect.stepLabel.recipient_identity') },
            { id: 'recipient_payout', label: t('wizard.collect.stepLabel.recipient_payout') },
            { id: 'funding_data', label: t('wizard.collect.stepLabel.funding_data') },
            { id: 'summary', label: t('wizard.collect.stepLabel.summary') },
          ]}
          activeIndex={3}
          completedIndices={[0, 1, 2]}
          onJump={(i) => {
            if (i === 0) navigate(SMART_ROUTES.COLLECT_RECIPIENT);
          }}
        />

        <div className="border-border bg-bg-elevated rounded-2xl border p-5">
          <div className="bg-primary-mesh rounded-2xl p-5">
            <p className="text-on-primary/80 text-[11px] font-semibold uppercase tracking-wide">
              {t('wizard.collect.summary.youPay.default')}
            </p>
            <p className="text-on-primary mt-1 text-3xl font-bold tracking-tight">
              {formatCurrency(quote.sendAmount, quote.sendCurrency)}
            </p>
            <p className="text-on-primary/80 mt-3 text-[11px] font-semibold uppercase tracking-wide">
              {t('wizard.collect.summary.recipientGets.default')}
            </p>
            <p className="text-on-primary mt-1 text-xl font-semibold">
              {formatCurrency(quote.receiveAmount, quote.receiveCurrency)}
            </p>
          </div>

          <dl className="mt-5 flex flex-col gap-3 text-sm">
            <SummaryRow
              label={t('wizard.collect.summary.rowLabel.recipient')}
              value={collect.recipientIdentity?.fullName ?? '—'}
              onEdit={() => navigate(SMART_ROUTES.COLLECT_RECIPIENT)}
              editLabel={t('common.edit')}
            />
            <SummaryRow
              label={t('wizard.collect.summary.rowLabel.delivery')}
              value={
                compose.payout
                  ? t(`methods.payout.${compose.payout}.displayName`)
                  : '—'
              }
            />
            <SummaryRow
              label={t('wizard.collect.summary.rowLabel.funding')}
              value={
                compose.funding
                  ? t(`methods.funding.${compose.funding}.displayName`)
                  : '—'
              }
            />
            <SummaryRow
              label={t('wizard.collect.summary.fee')}
              value={formatCurrency(quote.totalFee, quote.sendCurrency)}
            />
          </dl>

          {showFirstSendWarning && firstSend ? (
            <div className="mt-2.5 mb-2.5">
              <SmartFirstSendWarning copy={firstSend} />
            </div>
          ) : null}

          <Button
            fullWidth
            size="lg"
            className={showFirstSendWarning && firstSend ? "mt-0" : "mt-6"}
            onClick={() => navigate(SMART_ROUTES.KYC_INTRO)}
          >
            {t('wizard.collect.confirm.send')}
          </Button>
        </div>

        <div className="flex justify-start">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(SMART_ROUTES.COLLECT_RECIPIENT)}
          >
            ← {t('common.back')}
          </Button>
        </div>
      </div>
    </SmartShell>
  );
};

const SummaryRow = ({
  label,
  value,
  onEdit,
  editLabel,
}: {
  label: string;
  value: string;
  onEdit?: () => void;
  editLabel?: string;
}) => (
  <div className="border-border flex items-start justify-between gap-3 border-b pb-3 last:border-0 last:pb-0">
    <div className="min-w-0">
      <dt className="text-fg-muted text-[11px] font-semibold uppercase tracking-wide">
        {label}
      </dt>
      <dd className="text-fg mt-0.5 font-medium">{value}</dd>
    </div>
    {onEdit && (
      <button
        type="button"
        onClick={onEdit}
        className="text-accent shrink-0 text-xs font-semibold"
      >
        {editLabel}
      </button>
    )}
  </div>
);
